import { useEffect, useState } from "react";
import { api, eur, dateFr } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Leases() {
  const { user } = useAuth();
  const isOwner = user.role === "proprietaire" || user.role === "admin";
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", tenant_name: "", tenant_email: "", start_date: "", end_date: "", rent_amount: "", charges_amount: "", deposit: "" });

  const load = () => api.get("/leases").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if (isOwner) api.get("/properties").then((r) => setProps(r.data));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handoverKeys = async (id) => {
    try { await api.post(`/leases/${id}/handover-keys`); toast.success("Clés remises au locataire"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
  };

  const pickProperty = (id) => {
    const p = props.find((x) => x.id === id);
    setForm({ ...form, property_id: id, rent_amount: p?.rent_amount ?? "", charges_amount: p?.charges_amount ?? "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leases", { ...form, rent_amount: Number(form.rent_amount) || 0, charges_amount: Number(form.charges_amount) || 0, deposit: Number(form.deposit) || 0 });
      toast.success("Bail créé — échéancier de loyers généré");
      setOpen(false); setForm({ property_id: "", tenant_name: "", tenant_email: "", start_date: "", end_date: "", rent_amount: "", charges_amount: "", deposit: "" });
      load();
    } catch { toast.error("Erreur lors de la création du bail"); }
  };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title={isOwner ? "Baux" : "Mon bail"}
        subtitle="Contrats de location et échéanciers."
        action={isOwner && <Button onClick={() => setOpen(true)} className="rounded-full gap-2" data-testid="add-lease-btn"><Plus className="w-4 h-4" /> Nouveau bail</Button>}
      />

      {items.length === 0 ? (
        <EmptyState title="Aucun bail" desc={isOwner ? "Créez un bail pour générer automatiquement l'échéancier des loyers." : "Aucun bail actif pour le moment."} />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Locataire</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead>
                <TableHead>Loyer + charges</TableHead><TableHead>Dépôt</TableHead><TableHead>Statut</TableHead><TableHead>Bail / Clés</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
                  <TableCell className="font-medium">{l.tenant_name}</TableCell>
                  <TableCell>{dateFr(l.start_date)}</TableCell>
                  <TableCell>{dateFr(l.end_date)}</TableCell>
                  <TableCell>{eur(l.rent_amount + (l.charges_amount || 0))}</TableCell>
                  <TableCell>{eur(l.deposit)}</TableCell>
                  <TableCell><Badge>{l.status}</Badge></TableCell>
                  <TableCell>
                    {l.keys_handed_over ? (
                      <Badge className="bg-[hsl(var(--chart-2))] gap-1"><KeyRound className="w-3 h-3" /> Clés remises</Badge>
                    ) : l.bail_signed ? (
                      <Button size="sm" className="rounded-full gap-1" onClick={() => handoverKeys(l.id)} data-testid={`handover-${l.id}`}><KeyRound className="w-3.5 h-3.5" /> Remettre les clés</Button>
                    ) : (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Bail à signer</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau bail</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Bien</Label>
              <Select value={form.property_id} onValueChange={pickProperty}>
                <SelectTrigger data-testid="lease-property"><SelectValue placeholder="Sélectionner un bien" /></SelectTrigger>
                <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} — {p.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom locataire</Label><Input value={form.tenant_name} onChange={set("tenant_name")} required data-testid="lease-tenant-name" /></div>
              <div><Label>Email locataire</Label><Input type="email" value={form.tenant_email} onChange={set("tenant_email")} data-testid="lease-tenant-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de début</Label><Input type="date" value={form.start_date} onChange={set("start_date")} required data-testid="lease-start" /></div>
              <div><Label>Date de fin</Label><Input type="date" value={form.end_date} onChange={set("end_date")} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Loyer</Label><Input type="number" value={form.rent_amount} onChange={set("rent_amount")} required /></div>
              <div><Label>Charges</Label><Input type="number" value={form.charges_amount} onChange={set("charges_amount")} /></div>
              <div><Label>Dépôt</Label><Input type="number" value={form.deposit} onChange={set("deposit")} /></div>
            </div>
            <DialogFooter><Button type="submit" className="rounded-full" data-testid="save-lease-btn">Créer le bail</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
